<template>
  <div class="px-4 py-5">
    <LoadingState :loading="loading" :message="error" />

    <div v-if="job && !loading" class="space-y-4">
      <div class="flex items-start justify-between gap-3">
        <h1 class="text-xl font-extrabold text-slate-800 text-left leading-tight">{{ headingTitle }}</h1>
        <StatusBadge :status="displayStatus" />
      </div>

      <div
        v-for="(entry, index) in jobEntries"
        :key="'role-' + index"
        class="rounded-xl border border-slate-100 bg-slate-50 p-4 text-left text-sm text-slate-700 space-y-3"
      >
        <div class="space-y-1">
          <p class="font-bold text-slate-800">{{ index + 1 }}. Job Title: {{ display(entry.title) }}</p>
          <p><span class="font-bold text-slate-800">Job Type:</span> {{ display(entry.job_type) }}</p>
          <p><span class="font-bold text-slate-800">Education Level:</span> {{ display(entry.education_level) }}</p>
          <p><span class="font-bold text-slate-800">Experience:</span> {{ display(entry.experience) }}</p>
          <p><span class="font-bold text-slate-800">Sex:</span> {{ display(entry.sex) }}</p>
          <p><span class="font-bold text-slate-800">No Of Employees:</span> {{ display(entry.no_of_employees) }}</p>
          <!-- <p><span class="font-bold text-slate-800">Sector:</span> {{ display(entry.sector) }}</p> -->
          <p><span class="font-bold text-slate-800">Salary:</span> {{ display(entry.salary) }}</p>
        </div>

        <div class="border-t border-slate-200 pt-3 space-y-1.5">
          <p v-for="row in commonRows" :key="row.label">
            <span class="font-bold text-slate-800">{{ row.label }}:</span>
            {{ row.value }}
          </p>
        </div>

        <div>
          <p class="field-label mb-1">Description</p>
          <p class="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{{ job.description }}</p>
        </div>

        <div v-if="isOwner && job.status === 'published'" class="text-xs text-slate-500">
          Applications: {{ entryApplicationCount(index) }}
        </div>

        <div class="space-y-2 pt-1">
          <button
            v-if="isOwner && job.status === 'published'"
            type="button"
            class="btn-primary"
            @click="goApplicants(index)"
          >
            View Applicants
          </button>
          <button
            v-if="!isOwner && job.status === 'published'"
            type="button"
            class="btn-primary"
            :disabled="isExpired"
            @click="goApply(index)"
          >
            Apply
          </button>
        </div>
      </div>

      <div class="text-xs text-slate-500 space-y-1 text-left">
        <p v-if="jobFormPhone">Phone: {{ jobFormPhone }}</p>
        <template v-if="isOwner">
          <p v-if="!jobFormPhone && job.employer_phone">Phone: {{ job.employer_phone }}</p>
          <p v-if="tgUsername">Telegram: @{{ tgUsername }}</p>
        </template>
        <p>Posted: {{ formatDate(job.created_at) }}</p>
      </div>

      <div v-if="isOwner" class="pt-2">
        <button type="button" class="btn-secondary" @click="$router.push({ name: 'my_jobs' })">
          Back to My Jobs
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { apiFetch } from '../utils/api'
import { formatJobTitles, getJobDisplayStatus, getJobEntries, isJobExpired } from '../utils/jobFields'
import LoadingState from '../components/LoadingState.vue'
import StatusBadge from '../components/StatusBadge.vue'

export default {
  name: 'JobDetailView',
  components: { LoadingState, StatusBadge },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const store = useStore()
    const job = ref(null)
    const loading = ref(true)
    const error = ref('')

    const isOwner = computed(() => job.value && store.getters.currentUser?.id === job.value.employer_id)

    function formatDate(value) {
      if (!value) return ''
      return new Date(value).toLocaleString()
    }

    function display(value) {
      const v = value == null ? '' : String(value).trim()
      return v || '—'
    }

    const tgUsername = computed(() => {
      const j = job.value
      if (!j) return ''
      const u = j.telegram_username || j.employer_username
      return u ? String(u).replace(/^@/, '') : ''
    })

    const jobFormPhone = computed(() => {
      const p = job.value?.phone
      const v = p == null ? '' : String(p).trim()
      return v
    })

    const jobEntries = computed(() => getJobEntries(job.value))

    const headingTitle = computed(() => formatJobTitles(job.value?.title))
    const isExpired = computed(() => isJobExpired(job.value))
    const displayStatus = computed(() => getJobDisplayStatus(job.value))

    const commonRows = computed(() => {
      const j = job.value
      if (!j) return []
      return [
        { label: 'Company Name', value: display(j.company_name) },
        { label: 'Work Address', value: display(j.work_address) },
        { label: 'Work Location', value: display(j.work_location) },
        { label: 'Job Deadline', value: j.job_deadline ? String(j.job_deadline).slice(0, 10) : '—' },
      ]
    })

    function entryApplicationCount(index) {
      const counts = job.value?.applications_count_by_entry
      if (!counts) return 0
      const n = counts[index] ?? counts[String(index)]
      return Number(n) || 0
    }

    function goApplicants(index) {
      router.push({
        name: 'applicants',
        params: { id: job.value.id },
        query: { entry: String(index) },
      })
    }

    function goApply(index) {
      if (isExpired.value) return
      router.push({
        name: 'apply_job',
        params: { id: job.value.id },
        query: { entry: String(index) },
      })
    }

    async function load() {
      loading.value = true
      error.value = ''
      try {
        const res = await apiFetch(`/jobs/${route.params.id}`)
        const data = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(data.message || 'Job not found')
        job.value = data.job
      } catch (err) {
        error.value = err.message
      } finally {
        loading.value = false
      }
    }

    onMounted(load)
    return {
      job,
      loading,
      error,
      isOwner,
      jobEntries,
      headingTitle,
      isExpired,
      displayStatus,
      commonRows,
      jobFormPhone,
      tgUsername,
      display,
      formatDate,
      entryApplicationCount,
      goApplicants,
      goApply,
    }
  },
}
</script>
